import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { API } from '@/lib/data';
import { DEFAULT_SERVICES, DEFAULT_PROCESS_STEPS, DEFAULT_VIDEOS, DEFAULT_BLOGS } from '@/lib/defaults';
import { Reveal, HoverCard } from '@/components/Reveal';
import { SEO, createServiceSchema, createBreadcrumbSchema, createFAQSchema, createVideoSchema } from '@/components/SEO';
import { Breadcrumb } from '@/components/Breadcrumb';
import { ContactForm } from '@/components/ContactForm';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Stethoscope, Leaf, Heart, ArrowRight, CheckCircle, Play,
  MessageCircle, ClipboardList, FileText, TrendingUp, Phone, ChevronRight
} from 'lucide-react';
import { CONTACT_INFO } from '@/lib/data';

const iconMap = { Stethoscope, Leaf, Heart };
const processIcons = { MessageCircle, ClipboardList, FileText, TrendingUp };

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const defaultService = DEFAULT_SERVICES.find(s => s.id === slug) || null;
  const [service, setService] = useState(defaultService);
  const [processSteps, setProcessSteps] = useState(DEFAULT_PROCESS_STEPS);
  const [videos, setVideos] = useState(DEFAULT_VIDEOS.filter(v => v.service === slug).slice(0, 2));
  const [blogs, setBlogs] = useState(DEFAULT_BLOGS.filter(b => b.related_service === slug).slice(0, 1));

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/services/${slug}`),
      axios.get(`${API}/process-steps`),
      axios.get(`${API}/videos`),
      axios.get(`${API}/blog`),
    ]).then(([sRes, pRes, vRes, bRes]) => {
      setService(sRes.data);
      setProcessSteps(pRes.data);
      setVideos(vRes.data.filter(v => v.service === slug).slice(0, 2));
      setBlogs((bRes.data.items || bRes.data).filter(b => b.related_service === slug).slice(0, 1));
    }).catch(console.error);
    window.scrollTo(0, 0);
  }, [slug]);

  const serviceSchema = createServiceSchema(service);
  const breadcrumbSchema = createBreadcrumbSchema([
    { name: 'Ana Sayfa', url: '/' },
    { name: 'Tedaviler', url: '/hizmetler' },
    { name: service?.title || '' }
  ]);
  const serviceFaqSchema = service?.faq?.length ? createFAQSchema(service.faq.map(f => ({ q: f.q || f.question, a: f.a || f.answer }))) : null;
  const serviceVideoSchemas = videos?.slice(0, 3).map(createVideoSchema).filter(Boolean);

  if (!service) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-20 text-center">
        <h1 className="font-heading text-[30px] font-semibold mb-4">Tedavi bulunamadı</h1>
        <Link to="/hizmetler" className="text-[#1F6E7A] text-sm font-medium">← Tedavilere Dön</Link>
      </div>
    );
  }

  const IconComp = iconMap[service.icon] || Stethoscope;

  return (
    <div data-testid="service-detail-page">
      <SEO
        title={service?.title}
        description={service?.overview?.slice(0, 160)}
        schema={[serviceSchema, breadcrumbSchema, serviceFaqSchema, ...serviceVideoSchemas]}
      />
      <Breadcrumb items={[
        { label: 'Tedaviler', href: '/hizmetler' },
        { label: service?.title || '', href: `/hizmetler/${slug}`, current: true },
      ]} />
      <section className="bg-[#F5F3EF] py-16 sm:py-20 overflow-hidden">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10">
          <Reveal variant="fadeDown" duration={0.4}>
            <Link to="/hizmetler" className="text-sm text-[#5F6B76] hover:text-[#1F6E7A] mb-4 inline-block transition-colors">← Tedaviler</Link>
          </Reveal>
          <Reveal variant="fadeLeft" duration={0.6} delay={100}>
            <div className="flex items-center gap-3 mb-4">
              <motion.div
                className="w-12 h-12 rounded-2xl bg-[#1F6E7A]/10 flex items-center justify-center"
                whileHover={{ rotate: 10, scale: 1.1 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <IconComp size={24} className="text-[#1F6E7A]" />
              </motion.div>
              <h1 className="font-heading text-[34px] sm:text-[34px] font-semibold tracking-[-0.02em]">{service.title}</h1>
            </div>
          </Reveal>
          <Reveal variant="fadeLeft" duration={0.6} delay={200}>
            <p className="text-[22px] text-[#5F6B76] leading-relaxed max-w-2xl">{service.overview}</p>
          </Reveal>
          <Reveal variant="fadeUp" delay={350}>
            <div className="flex flex-wrap gap-3 mt-6">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/iletisim"
                  className="inline-flex items-center px-6 py-3 rounded-full bg-[#1F6E7A] text-white text-sm font-semibold shadow-[0_8px_24px_-12px_rgba(31,110,122,0.4)] hover:bg-[#185960] transition-colors"
                  data-testid="service-detail-primary-cta-button"
                >
                  Randevu Al
                </Link>
              </motion.div>
              <motion.a
                href={`https://wa.me/${CONTACT_INFO.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#E7E5E4] text-sm font-medium hover:bg-white transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <MessageCircle size={16} /> WhatsApp
              </motion.a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 2) What it covers — numbered steps */}
      <section className="bg-[#F5F3EF] py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10">
          <Reveal variant="fadeUp">
            <h2 className="font-heading text-[30px] font-semibold mb-6">Neleri kapsar?</h2>
          </Reveal>
          <div className="space-y-3">
            {service.covers.map((item, i) => (
              <Reveal key={i} variant="fadeLeft" delay={i * 90}>
                <motion.div
                  className="flex items-start gap-3"
                  whileHover={{ x: 6 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <motion.span
                    className="w-6 h-6 rounded-full bg-[#1F6E7A]/10 flex items-center justify-center shrink-0 text-xs font-semibold text-[#1F6E7A]"
                    whileHover={{ scale: 1.2, backgroundColor: 'rgba(31,110,122,0.2)' }}
                  >
                    {i + 1}
                  </motion.span>
                  <span className="text-[14px] text-[#1F2937]">{item}</span>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 4) Process — timeline */}
      <section className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10 py-16">
        <Reveal variant="fadeUp">
          <h2 className="font-heading text-[30px] font-semibold mb-8">4 Adımda Sağlık Yolculuğu</h2>
        </Reveal>

        {/* Desktop */}
        <div className="hidden lg:flex items-start justify-center gap-0">
          {processSteps.map((step, i) => {
            const PIcon = processIcons[step.icon] || MessageCircle;
            return (
              <Reveal key={step.step} variant="fadeUp" delay={i * 120}>
                <div className="flex items-start">
                  <div className="flex flex-col items-center" style={{ width: 180 }}>
                    <div className="w-14 h-14 rounded-full bg-white border-2 border-[#1F6E7A]/30 flex items-center justify-center shadow-sm">
                      <PIcon size={22} className="text-[#1F6E7A]" />
                    </div>
                    <div className="text-[12px] font-semibold text-[#1F6E7A]/60 mt-2 mb-1">Adım {step.step}</div>
                    <h3 className="font-heading font-semibold text-[14px] text-center leading-snug">{step.title}</h3>
                  </div>
                  {i < processSteps.length - 1 && (
                    <div className="flex items-center h-14 px-1">
                      <div className="w-10 h-[2px] bg-gradient-to-r from-[#1F6E7A]/30 to-[#1F6E7A]/15" />
                      <ChevronRight size={20} className="text-[#1F6E7A]/40 -ml-1" />
                    </div>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Mobile */}
        <div className="lg:hidden flex flex-col relative ml-6">
          <div className="absolute top-5 bottom-5 left-[18px] w-[2px] bg-gradient-to-b from-[#1F6E7A]/30 to-[#1F6E7A]/10" />
          {processSteps.map((step, i) => {
            const PIcon = processIcons[step.icon] || MessageCircle;
            return (
              <Reveal key={step.step} variant="fadeRight" delay={i * 100}>
                <div className="flex items-center gap-5 py-4">
                  <div className="w-[38px] h-[38px] min-w-[38px] rounded-full bg-white border-2 border-[#1F6E7A]/30 flex items-center justify-center shadow-sm z-10">
                    <PIcon size={16} className="text-[#1F6E7A]" />
                  </div>
                  <div>
                    <span className="text-[12px] font-semibold text-[#1F6E7A]/60 block">Adım {step.step}</span>
                    <h3 className="font-heading font-semibold text-[14px] leading-snug">{step.title}</h3>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* 5) FAQ — animated accordion */}
      <section className="bg-[#F5F3EF] py-16">
        <div className="mx-auto max-w-2xl px-4 sm:px-6">
          <Reveal variant="fadeUp">
            <h2 className="font-heading text-[30px] font-semibold mb-8 text-center">Sık Sorulan Sorular</h2>
          </Reveal>
          <Reveal variant="fadeUp" delay={100}>
            <Accordion type="single" collapsible className="space-y-3">
              {service.faq.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="bg-white rounded-[20px] border border-[#E7E5E4] px-5 overflow-hidden data-[state=open]:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.08)] data-[state=open]:border-[#1F6E7A]/30 transition-all"
                >
                  <AccordionTrigger className="text-[14px] font-semibold text-left py-5 hover:no-underline [&[data-state=open]>svg]:rotate-45">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[14px] text-[#5F6B76] leading-relaxed pb-5">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* 6) Related content — hover cards */}
      <section className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10 py-16">
        <Reveal variant="fadeUp">
          <h2 className="font-heading text-[30px] font-semibold mb-8">İlgili İçerikler</h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {videos.map((v, i) => (
            <Reveal key={v.id} variant="fadeLeft" delay={i * 120}>
              <HoverCard>
                <div className="bg-white rounded-[20px] border border-[#E7E5E4] overflow-hidden">
                  <div className="relative aspect-video bg-gray-100 overflow-hidden">
                    <motion.img
                      src={`https://img.youtube.com/vi/${v.id}/mqdefault.jpg`}
                      alt={v.title}
                      className="w-full h-full object-cover"
                      loading="eager"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.4 }}
                    />
                    <a href={`https://www.youtube.com/watch?v=${v.id}`} target="_blank" rel="noopener noreferrer" className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors">
                      <motion.div
                        className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg"
                        whileHover={{ scale: 1.15 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <Play size={20} className="text-[#1F6E7A] ml-0.5" fill="currentColor" />
                      </motion.div>
                    </a>
                  </div>
                  <div className="p-4">
                    <h3 className="text-[14px] font-medium">{v.title}</h3>
                  </div>
                </div>
              </HoverCard>
            </Reveal>
          ))}
          {blogs.map((b, i) => (
            <Reveal key={b.id} variant="fadeRight" delay={(videos.length + i) * 120}>
              <HoverCard>
                <Link to={`/icerikler/${b.id}`} className="block bg-white rounded-[20px] border border-[#E7E5E4] overflow-hidden">
                  <div className="aspect-video bg-gray-100 overflow-hidden">
                    <motion.img
                      src={b.image}
                      alt={b.title}
                      className="w-full h-full object-cover"
                      loading="eager"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.4 }}
                    />
                  </div>
                  <div className="p-4">
                    <span className="text-xs text-[#1F6E7A] font-medium">{b.category}</span>
                    <h3 className="text-[14px] font-medium mt-1">{b.title}</h3>
                  </div>
                </Link>
              </HoverCard>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 7) CTA — large contact buttons */}
      <section className="py-16">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 text-center">
          <Reveal variant="fadeUp">
            <h2 className="font-heading text-[30px] sm:text-[34px] font-semibold mb-3">Detaylı bilgi için</h2>
            <p className="text-[14px] text-[#5F6B76] mb-8">Hemen bize ulaşın, sorularınızı yanıtlayalım.</p>
          </Reveal>
          <Reveal variant="fadeUp" delay={150}>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <motion.a
                href={`tel:${CONTACT_INFO.phone}`}
                data-testid="service-detail-call-btn"
                className="flex items-center justify-center gap-3 px-8 py-5 rounded-2xl bg-[#1F6E7A] text-white text-[14px] font-semibold shadow-[0_8px_24px_-12px_rgba(31,110,122,0.4)] transition-colors hover:bg-[#185960]"
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.97 }}
              >
                <Phone size={24} /> Bizi Arayın
              </motion.a>
              <motion.a
                href={`https://wa.me/${CONTACT_INFO.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="service-detail-whatsapp-btn"
                className="flex items-center justify-center gap-3 px-8 py-5 rounded-2xl bg-[#25D366] text-white text-[14px] font-semibold shadow-[0_8px_24px_-12px_rgba(37,211,102,0.4)] transition-colors hover:bg-[#1EBE5D]"
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.97 }}
              >
                <MessageCircle size={24} /> WhatsApp
              </motion.a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 8) Appointment — form card */}
      <section className="bg-[#F5F3EF] py-16">
        <div className="mx-auto max-w-2xl px-4 sm:px-6">
          <Reveal variant="scaleUp">
            <motion.div
              className="bg-white rounded-[28px] border border-[#E7E5E4] p-6 sm:p-8"
              whileHover={{ boxShadow: '0 20px 40px -20px rgba(0,0,0,0.15)' }}
              transition={{ duration: 0.3 }}
            >
              <h2 className="font-heading text-[24px] font-semibold mb-2 text-center">Randevu Alın</h2>
              <p className="text-[14px] text-[#5F6B76] text-center mb-6">Bu tedavi hakkında detaylı bilgi veya randevu için formu doldurun.</p>
              <ContactForm compact />
            </motion.div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
